/**
 * ConsolePage — lukhas.io
 *
 * Full-page self-contained Constellation console. Uses static data with a
 * simulated live feed. Matches the lukhas.io aesthetic (Helvetica Neue
 * ultra-light, vision-teal, framer-motion, SiteHeader/SiteFooter).
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Terminal } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

// ── Typography tokens (matches lukhas.io pattern) ────────────────────────────

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

// ── CLI design tokens ─────────────────────────────────────────────────────────

const C = {
  bg:           '#08090C',
  surface:      '#0E1015',
  surfaceHover: '#13161D',
  border:       '#1A1E28',
  borderSubtle: '#14171F',
  accent:       '#C8CCD8',
  text:         '#A0A6B4',
  textDim:      '#5A6070',
  textGhost:    '#333844',
  white:        '#E8ECF2',
  lambda:       'rgba(200, 204, 216, 0.9)',
  lambdaGlow:   'rgba(200, 204, 216, 0.04)',
} as const

const DISPLAY = '"DM Sans", "Helvetica Neue", -apple-system, sans-serif'
const MONO_STACK = '"SF Mono", "Cascadia Code", "JetBrains Mono", "Fira Code", monospace'

// ── Data ──────────────────────────────────────────────────────────────────────

interface StarEntry {
  code: string; name: string; status: string; health: number
}

interface LogEntry {
  time: string; message: string; type: 'info' | 'success' | 'warn'
}

const BASE_STARS: StarEntry[] = [
  { code: 'IDN', name: 'Identity',  status: 'secured',  health: 98 },
  { code: 'MEM', name: 'Memory',    status: 'syncing',   health: 95 },
  { code: 'VIS', name: 'Vision',    status: 'online',    health: 100 },
  { code: 'BIO', name: 'Bio',       status: 'online',    health: 97 },
  { code: 'DRM', name: 'Dream',     status: 'standby',   health: 88 },
  { code: 'ETH', name: 'Ethics',    status: 'watching',  health: 100 },
  { code: 'GRD', name: 'Guardian',  status: 'online',    health: 99 },
  { code: 'QNT', name: 'Quantum',   status: 'standby',   health: 92 },
]

const PIPELINE = [
  { letter: 'M', label: 'Memory' },
  { letter: 'A', label: 'Attention' },
  { letter: 'T', label: 'Thought' },
  { letter: 'R', label: 'Risk' },
  { letter: 'I', label: 'Intent' },
  { letter: 'Z', label: 'Action' },
]

const LOG_MESSAGES: Array<Omit<LogEntry, 'time'>> = [
  { message: 'constellation health check passed',       type: 'success' },
  { message: 'identity token refreshed — sovereign',   type: 'success' },
  { message: 'ethics watchdog sweep complete',          type: 'success' },
  { message: 'dream engine entering standby',           type: 'info'    },
  { message: 'memory fold operation completed',         type: 'info'    },
  { message: 'guardian drift-check nominal',            type: 'success' },
  { message: 'quantum coherence within bounds',         type: 'info'    },
  { message: 'bio adaptation cycle triggered',          type: 'info'    },
]

function nowTime(): string {
  return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

// ── Stagger hook ──────────────────────────────────────────────────────────────

function useStagger(count: number, base = 80, start = 600): boolean[] {
  const [vis, setVis] = useState<boolean[]>(Array(count).fill(false))
  useEffect(() => {
    const ts = Array.from({ length: count }, (_, i) =>
      setTimeout(() => setVis(prev => { const n = [...prev]; n[i] = true; return n }), start + i * base)
    )
    return () => ts.forEach(clearTimeout)
  }, [count, base, start])
  return vis
}

// ── Star table row (needs its own hook scope) ─────────────────────────────────

function StarTableRow({ star, idx, visible }: { star: StarEntry; idx: number; visible: boolean }) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px',
        alignItems: 'center', padding: '14px 20px', cursor: 'default',
        background: hov ? C.surfaceHover : 'transparent',
        borderBottom: `1px solid ${C.borderSubtle}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-12px)',
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, background 0.2s ease`,
      }}
    >
      <span style={{ fontFamily: MONO_STACK, fontSize: 11, fontWeight: 500, color: C.accent, letterSpacing: '0.06em' }}>{star.code}</span>
      <span style={{ fontFamily: DISPLAY, fontSize: 14, color: hov ? C.white : C.text, transition: 'color 0.2s' }}>{star.name}</span>
      <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{star.status}</span>
      <div style={{ height: 2, background: C.border, borderRadius: 1, overflow: 'hidden' }}>
        <div style={{ width: `${star.health}%`, height: '100%', background: C.accent, opacity: 0.5, borderRadius: 1, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
      </div>
      <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim, textAlign: 'right', opacity: hov ? 1 : 0, transition: 'opacity 0.2s' }}>&#8594;</span>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ConsolePage() {
  const [mounted, setMounted] = useState(false)
  const [headerVis, setHeaderVis] = useState(false)
  const [metricsVis, setMetricsVis] = useState(false)
  const [activeStage, setActiveStage] = useState(2)
  const [cycle, setCycle] = useState(48291)
  const [entropy, setEntropy] = useState(0.0023)
  const [log, setLog] = useState<LogEntry[]>([
    { time: '00:02:41', message: 'constellation health check passed',     type: 'success' },
    { time: '00:02:38', message: 'matriz cycle #48,291 started',          type: 'info'    },
    { time: '00:02:34', message: 'identity token refreshed — sovereign',  type: 'success' },
    { time: '00:02:30', message: 'dream engine entering standby',         type: 'info'    },
    { time: '00:02:22', message: 'ethics watchdog sweep complete',        type: 'success' },
  ])

  useEffect(() => {
    setMounted(true)
    const t1 = setTimeout(() => setHeaderVis(true), 200)
    const t2 = setTimeout(() => setMetricsVis(true), 500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStage(prev => (prev + 1) % PIPELINE.length)
      setCycle(prev => prev + 1)
      setEntropy(prev => Math.max(0.0001, prev + (Math.random() - 0.5) * 0.0002))
      const pick = LOG_MESSAGES[Math.floor(Math.random() * LOG_MESSAGES.length)]
      setLog(prev => [{ ...pick, time: nowTime() }, ...prev].slice(0, 5))
    }, 5000)
    return () => clearInterval(id)
  }, [])

  const starsVis = useStagger(BASE_STARS.length, 70, 800)
  const pipeVis = useStagger(PIPELINE.length, 100, 400)
  const actVis = useStagger(log.length, 60, 1000)

  const metrics = [
    { label: 'Modules', value: '692',                    unit: 'loaded' },
    { label: 'Uptime',  value: '99.99',                  unit: '%' },
    { label: 'Entropy', value: entropy.toFixed(4),       unit: '' },
    { label: 'Cycle',   value: `#${cycle.toLocaleString()}`, unit: '' },
  ]

  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      {/* Page hero */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-white/35 hover:text-white/65 transition-colors mb-10"
              style={bodyFont}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vision-teal/10 border border-vision-teal/25 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-vision-teal animate-pulse" />
              <span className="text-vision-teal text-xs tracking-[0.15em] uppercase" style={bodyFont}>
                Constellation Console
              </span>
            </div>

            <h1
              className="text-5xl md:text-6xl text-white tracking-[0.12em] mb-4"
              style={brandFont}
            >
              &#923; Console
            </h1>
            <p className="text-xl text-white/55 max-w-2xl" style={bodyFont}>
              Live view of the 8-star Constellation framework. Identity, memory, ethics, and all
              cognitive subsystems in one terminal-style panel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLI panel */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Rounded container that frames the dark CLI */}
            <div className="rounded-2xl overflow-hidden border border-white/[0.07]" style={{ background: C.bg, color: C.text, fontFamily: DISPLAY }}>

              {/* Header row */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '32px 32px 0',
                opacity: headerVis ? 1 : 0,
                transform: headerVis ? 'translateY(0)' : 'translateY(-10px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div style={{ position: 'relative', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', inset: -20, borderRadius: '50%', background: `radial-gradient(circle, ${C.lambdaGlow} 0%, transparent 70%)`, animation: 'con-breathe 4s ease-in-out infinite' }} />
                    <span style={{ fontFamily: DISPLAY, fontSize: 38, fontWeight: 300, color: C.lambda, letterSpacing: '-0.02em', position: 'relative', zIndex: 1 }}>&#923;</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 300, color: C.white, letterSpacing: '0.08em' }}>LUKH&#923;S</div>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', marginTop: 2 }}>CONSCIOUSNESS BY DESIGN</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>MODE</div>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 11, color: C.textDim, marginTop: 2 }}>simulated</div>
                  </div>
                  <div style={{ width: 1, height: 28, background: C.border }} />
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>TIER</div>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 11, color: C.accent, marginTop: 2 }}>SOVEREIGN</div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: C.border, margin: '24px 32px', opacity: headerVis ? 1 : 0, transform: headerVis ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 400ms' }} />

              {/* Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, margin: '0 32px 24px', background: C.border, border: `1px solid ${C.border}` }}>
                {metrics.map((m, i) => (
                  <div key={m.label} style={{ background: C.surface, padding: '18px 20px' }}>
                    <div style={{ opacity: metricsVis ? 1 : 0, transform: metricsVis ? 'translateY(0)' : 'translateY(8px)', transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms` }}>
                      <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>{m.label}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                        <span style={{ fontFamily: MONO_STACK, fontSize: 20, fontWeight: 400, color: C.white, letterSpacing: '-0.02em' }}>{m.value}</span>
                        {m.unit && <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim }}>{m.unit}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Main 2-col grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 1, margin: '0 32px', background: C.border, border: `1px solid ${C.border}` }}>

                {/* Left: star table */}
                <div style={{ background: C.surface }}>
                  <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '0 0', opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 600ms' }}>Constellation</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px', alignItems: 'center', padding: '8px 20px', borderBottom: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease 700ms' }}>
                    {['Code', 'Module', 'Status', 'Health', ''].map(h => (
                      <span key={h} style={{ fontFamily: MONO_STACK, fontSize: 9, color: C.textGhost, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{h}</span>
                    ))}
                  </div>
                  {BASE_STARS.map((s, i) => (
                    <StarTableRow key={s.code} star={s} idx={i} visible={starsVis[i]} />
                  ))}
                </div>

                {/* Right: pipeline + log */}
                <div style={{ background: C.surface, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '16px 20px 20px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2, opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 300ms' }}>MATRIZ Pipeline</div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16, position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 18, left: 18, right: 18, height: 1, background: C.border, zIndex: 0 }} />
                      <div style={{ position: 'absolute', top: 18, left: 18, width: mounted ? `${(activeStage / (PIPELINE.length - 1)) * 100}%` : '0%', height: 1, background: C.textDim, zIndex: 0, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
                      {PIPELINE.map((s, i) => (
                        <div key={s.letter} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: pipeVis[i] ? 1 : 0, transform: pipeVis[i] ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.95)', transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${400 + i * 100}ms` }}>
                          <div style={{ width: 36, height: 36, border: `1.5px solid ${i === activeStage ? C.accent : i < activeStage ? C.textDim : C.border}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO_STACK, fontSize: 13, fontWeight: 500, color: i === activeStage ? C.white : i < activeStage ? C.textDim : C.textGhost, background: i === activeStage ? `${C.accent}0A` : 'transparent', transition: 'all 0.6s ease', position: 'relative' }}>
                            {s.letter}
                            {i === activeStage && <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: `1px solid ${C.accent}15`, animation: 'con-pulse 3s ease-in-out infinite' }} />}
                          </div>
                          <span style={{ fontFamily: MONO_STACK, fontSize: 9, color: i === activeStage ? C.text : C.textGhost, letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'color 0.3s' }}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, padding: '10px 0 0', borderTop: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1000ms' }}>
                      <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost }}>CYCLE</span>
                      <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textDim }}>#{cycle.toLocaleString()}</span>
                    </div>
                  </div>

                  <div style={{ flex: 1, padding: '16px 20px' }}>
                    <div style={{ fontFamily: MONO_STACK, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10, opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 800ms' }}>Activity</div>
                    {log.map((a, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '68px 1fr', gap: 12, padding: '7px 0', opacity: actVis[i] ? 1 : 0, transform: actVis[i] ? 'translateY(0)' : 'translateY(4px)', transition: `all 0.4s cubic-bezier(0.16,1,0.3,1) ${600 + i * 60}ms` }}>
                        <span style={{ fontFamily: MONO_STACK, fontSize: 11, color: C.textGhost }}>{a.time}</span>
                        <span style={{ fontFamily: MONO_STACK, fontSize: 11, color: a.type === 'success' ? C.text : C.textDim }}>{a.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Read-only prompt */}
              <div style={{ margin: '0 32px', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 10, opacity: mounted ? 1 : 0, transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 1200ms' }}>
                  <span style={{ fontFamily: MONO_STACK, fontSize: 12, color: C.textDim }}>$</span>
                  <div style={{ flex: 1, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
                    <span style={{ fontFamily: MONO_STACK, fontSize: 12, color: C.textGhost }}>get an api key at lukhas.id to connect a live terminal session...</span>
                    <span style={{ display: 'inline-block', width: 1, height: 14, background: C.accent, marginLeft: 2, verticalAlign: 'middle', animation: 'con-blink 1.2s steps(1) infinite', opacity: 0.6 }} />
                  </div>
                </div>
              </div>

              {/* Footer bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 32px 24px', opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1400ms' }}>
                <span style={{ fontFamily: MONO_STACK, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>LUKH&#923;S v4.2.1 — 8-STAR CONSTELLATION FRAMEWORK</span>
                <span style={{ fontFamily: MONO_STACK, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>692 MODULES — SIMULATED</span>
              </div>

              <style>{`
                @keyframes con-breathe {
                  0%, 100% { transform: scale(1); opacity: 0.6; }
                  50%       { transform: scale(1.15); opacity: 1; }
                }
                @keyframes con-pulse {
                  0%, 100% { transform: scale(1); opacity: 0.4; }
                  50%       { transform: scale(1.3); opacity: 0; }
                }
                @keyframes con-blink {
                  0%, 100% { opacity: 0.6; }
                  50%       { opacity: 0; }
                }
              `}</style>
            </div>
          </motion.div>

          {/* Auth CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 rounded-2xl border border-vision-teal/20 bg-vision-teal/5 p-8"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Terminal className="w-5 h-5 text-vision-teal" strokeWidth={1.5} />
                  <h3 className="text-lg text-white tracking-wide" style={bodyFont}>Connect a live terminal</h3>
                </div>
                <p className="text-sm text-white/50 max-w-lg" style={bodyFont}>
                  Authenticate with your &#923;iD passkey to connect this console to live Constellation data and
                  execute commands against the LUKHAS API.
                </p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <a
                  href="https://lukhas.id/login"
                  className="px-6 py-2.5 rounded-full bg-vision-gradient text-white text-sm tracking-[0.06em] hover:opacity-90 transition-opacity whitespace-nowrap"
                  style={bodyFont}
                >
                  Get API Key
                </a>
                <a
                  href="https://lukhas.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2.5 rounded-full border border-vision-teal/35 text-vision-teal text-sm tracking-[0.06em] hover:border-vision-teal/60 transition-all whitespace-nowrap"
                  style={bodyFont}
                >
                  Full Docs
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
